import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import type { SeatType } from '@/types';

import {
  liveCohortFilter,
  SESSION_FILTERS,
  useMembersFilterParams,
} from '@/features/companyDashboard/common/members/useMembersFilterParams';
import { useGetCompanyCohortTagsQuery } from '@/store/api/cohortTagApi';
import { type MembersTableParams } from '@/store/api/userApi';

export type CompanyMembersPage = 'partner' | 'ops';

export const MEMBER_STATUSES_KEY = 'member_statuses';
export const END_DATE_STATUSES_KEY = 'end_date_statuses';
export const INSIGHTS_STATUSES_KEY = 'insights_statuses';
export const SEAT_TYPES_KEY = 'seat_types';
export const COHORT_TAG_IDS_KEY = 'cohort_tag_ids';

// The one source of a company page's member filters, shared by its table and its CSV export so
// the file lists exactly the members on screen. The Ops page offers fewer filters, so it never
// applies search, end date status, or insights status, even if they are in the URL.
export const useCompanyMembersParams = (
  page: CompanyMembersPage,
): MembersTableParams => {
  const { companyId = '' } = useParams();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { arrayFilter } = useMembersFilterParams();
  const { data: cohortTags = [], isSuccess: cohortTagsLoaded } =
    useGetCompanyCohortTagsQuery(companyId, { skip: !companyId });

  const sessionFilter = SESSION_FILTERS.filter(
    (filter) => searchParams.get(filter) === 'true',
  );
  const params: MembersTableParams = {
    chemCall: sessionFilter.includes('chem_call') || undefined,
    cohortTagIds: liveCohortFilter(
      arrayFilter(COHORT_TAG_IDS_KEY),
      cohortTags,
      cohortTagsLoaded,
    ),
    companyIds: [companyId],
    companyMemberStatus: pathname.includes('past') ? 'past' : 'current',
    memberStatuses: arrayFilter(MEMBER_STATUSES_KEY),
    noUpcomingSessions:
      sessionFilter.includes('no_upcoming_sessions') || undefined,
    scheduledSessions:
      sessionFilter.includes('scheduled_sessions') || undefined,
    seatTypes: arrayFilter(SEAT_TYPES_KEY) as SeatType[],
  };

  if (page === 'ops') return params;

  return {
    ...params,
    endDateStatuses: arrayFilter(END_DATE_STATUSES_KEY),
    insightsStatuses: arrayFilter(INSIGHTS_STATUSES_KEY),
    search: searchParams.get('search') || undefined,
  };
};
